import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { catchError, finalize, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { City } from '../../../models/common/city.model';
import { Country } from '../../../models/common/country.model';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { PartyService } from '../../../party/party.service';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { gccCrNumberValidator, gccRegistrationError, gccVatNumberValidator } from '../../crm-gcc-registration.validation';
import { CRM_CLIENT_DOCUMENT_TYPES, CRM_REGISTRATION_ATTACHMENT_TYPES, CrmClient, CrmClientAddress, CrmClientCommunication, CrmClientDocument, CrmClientRegistrationAttachment, CrmRegistrationAttachmentCategory, CrmRoleOption } from '../../crm.model';
import { CrmService } from '../../crm.service';
import { OrdersService } from '../../../order/order.service';
import { PaymentTerm } from '../../../order/models/payment-term.model';

type ClientAttachmentSource = 'registration' | 'document';

interface ClientAttachmentOption {
  label: string;
  value: string;
  source: ClientAttachmentSource;
}

interface PendingClientAttachment {
  queueId: string;
  file: File;
  category: string;
  categoryLabel: string;
  source: ClientAttachmentSource;
  title?: string;
  expiryDate?: Date | string;
}

interface ClientAttachmentUploadResult {
  queueId: string;
  source: ClientAttachmentSource;
  success: boolean;
  record?: CrmClientDocument | CrmClientRegistrationAttachment;
}

export interface QueuedClientLogo {
  file: File;
  previewUrl: string;
  progress: number;
  state: 'queued' | 'uploading' | 'uploaded' | 'error';
}

@Component({
  selector: 'app-client-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    CheckboxModule,
    BreadcrumbModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    SelectButtonModule,
    TextareaModule,
    ToastModule,
    ToggleSwitchModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientFormComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private baseMasterDataService = inject(BaseMasterDataService);
  private sessionStorageService = inject(SessionStorageService);
  private messageService = inject(MessageService);
  private partyService = inject(PartyService);
  private ordersService = inject(OrdersService);
  private firebaseStorageService = inject(FirebaseStorageService);

  clientId = signal<number | null>(null);
  loading = signal(false);
  saving = signal(false);
  submitted = signal(false);
  supplierRoleSelected = signal(false);
  customerRoleSelected = signal(false);
  returnUrl = signal<string | null>(null);
  countries = signal<Country[]>([]);
  cities = signal<City[]>([]);
  crmRoles = signal<CrmRoleOption[]>([]);
  paymentTerms = signal<PaymentTerm[]>([]);
  documents = signal<CrmClientDocument[]>([]);
  registrationAttachments = signal<CrmClientRegistrationAttachment[]>([]);
  pendingAttachments = signal<PendingClientAttachment[]>([]);
  queuedLogo = signal<QueuedClientLogo | null>(null);
  private loadedCityCountryIds = new Set<number>();
  private loadingCityCountryIds = new Set<number>();
  private attachmentSequence = 0;
  employeeOptions = signal<{ label: string; value: number }[]>([]);
  private loadedCommunicationIds = new Set<number>();
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems = signal<MenuItem[]>([]);

  businessTypes = [
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' }
  ];

  categories = [
    { label: 'Standard', value: 'STANDARD' },
    { label: 'Premium', value: 'PREMIUM' },
    { label: 'VIP', value: 'VIP' },
    { label: 'Partner', value: 'PARTNER' }
  ];

  creditCheckModes = [
    { label: 'Enforce and Block', value: 'ENFORCE' },
    { label: 'Warn Only', value: 'WARN' },
    { label: 'Disabled', value: 'DISABLED' }
  ];

  communicationTypes = [
    { label: 'Primary', value: 'PRIMARY' },
    { label: 'Operations', value: 'OPERATION' },
    { label: 'Finance', value: 'FINANCE' },
    { label: 'Compliance', value: 'COMPLIANCE' },
    { label: 'Management', value: 'MANAGEMENT' },
    { label: 'Other', value: 'OTHER' }
  ];

  attachmentTypes: ClientAttachmentOption[] = CRM_CLIENT_DOCUMENT_TYPES.map(type => ({
    ...type,
    source: CRM_REGISTRATION_ATTACHMENT_TYPES.some(registrationType => registrationType.value === type.value)
      ? 'registration'
      : 'document'
  }));

  currencyOptions = computed(() => {
    const seen = new Set<string>();
    return this.countries()
      .filter(country => !!country.currencyCode)
      .map(country => ({ label: `${country.currencyCode} ${country.currencyLong || ''}`.trim(), value: country.currencyCode }))
      .filter(option => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
  });

  clientForm = this.fb.group({
    id: [null as number | null],
    companyName: ['', Validators.required],
    logoUrl: [''],
    customerNameLocal: ['', Validators.required],
    notifyingPersonName: ['', Validators.required],
    postingDate: [new Date() as Date | string | null, Validators.required],
    currencyCode: ['SAR', Validators.required],
    exchangeRate: [1, Validators.required],
    creditLimit: [null as number | null, Validators.min(0)],
    creditPeriodDays: [null as number | null, Validators.min(0)],
    defaultPaymentTermId: [null as number | null],
    creditCheckMode: ['ENFORCE' as 'ENFORCE' | 'WARN' | 'DISABLED'],
    creditHold: [false],
    creditHoldReason: [''],
    overdueToleranceDays: [0, Validators.min(0)],
    businessType: ['B2B' as 'B2B' | 'B2C', Validators.required],
    roles: [['CUSTOMER'] as string[], Validators.required],
    natureOfBusiness: [''],
    taxRegNumber: ['', [gccVatNumberValidator(() => this.selectedCountry())]],
    crNumber: [null as number | null, [gccCrNumberValidator(() => this.selectedCountry())]],
    crExpiryDate: [null as Date | string | null],
    contractExpiryDate: [null as Date | string | null],
    remarks: [''],
    primaryEmail: ['', [Validators.required, Validators.email]],
    phoneNumber: [''],
    mobileNumber: [''],
    postalCode: [''],
    countryId: [null as number | null],
    category: ['STANDARD'],
    website: [''],
    source: [''],
    kamCustomerId: [null as number | null],
    linkedCompanyId: [null as number | null],
    linkedCustomerId: [null as number | null],
    sourceLeadId: [null as number | null],
    companyId: [null as number | null],
    createdAt: [null as Date | null],
    createdByCustomerId: [null as number | null]
  });

  addressForm = this.fb.group({
    id: [null as number | null],
    street1: ['', Validators.required],
    street1Local: ['', Validators.required],
    street2: [''],
    buildingNumber: [''],
    plotIdentification: [''],
    postalCode: [''],
    city: [''],
    cityId: [null as number | null, Validators.required],
    cityLocal: ['', Validators.required],
    countryId: [null as number | null, Validators.required]
  });

  communicationsForm = this.fb.group({
    contacts: this.fb.array<FormGroup>([])
  });

  attachmentForm = this.fb.group({
    category: ['GENERAL'],
    title: [''],
    expiryDate: [null as Date | string | null]
  });

  ngOnInit() {
    this.loadCountries();
    this.loadRoles();
    this.loadEmployees();
    this.loadPaymentTerms();
    const categoryParam = this.route.snapshot.queryParamMap.get('category');
    const requestedCategory = categoryParam && this.categories.some(category => category.value === categoryParam)
      ? categoryParam
      : 'STANDARD';
    this.returnUrl.set(this.route.snapshot.queryParamMap.get('returnUrl'));
    this.clientForm.get('businessType')?.valueChanges.subscribe(() => this.syncBusinessTypeValidators());
    this.clientForm.get('roles')?.valueChanges.subscribe(roles => {
      this.supplierRoleSelected.set(this.hasSupplierRole(roles));
      this.customerRoleSelected.set(this.hasCustomerRole(roles));
      this.syncBusinessTypeValidators();
    });
    this.clientForm.get('creditHold')?.valueChanges.subscribe(() => this.syncCreditHoldValidator());
    this.addressForm.get('countryId')?.valueChanges.subscribe(countryId => {
      this.clientForm.patchValue({ countryId: countryId || null }, { emitEvent: false });
      this.clientForm.get('crNumber')?.updateValueAndValidity({ emitEvent: false });
      this.clientForm.get('taxRegNumber')?.updateValueAndValidity({ emitEvent: false });
      const currentCityId = this.addressForm.get('cityId')?.value;
      if (!countryId || (currentCityId && !this.cityBelongsToCountry(currentCityId, countryId))) {
        this.addressForm.patchValue({ cityId: null, city: '' }, { emitEvent: false });
      }
      this.loadCitiesForCountry(countryId);
    });
    this.syncBusinessTypeValidators();
    this.syncCreditHoldValidator();
    this.addContact({ communicationType: 'PRIMARY', primaryRecord: true });

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.clientId.set(id);
      this.setBreadcrumb('Edit Client');
      this.loadClient(id);
      return;
    }

    const tenantCountryId = this.toolbarService.customer()?.company?.countryId ?? null;
    this.clientForm.patchValue({
      postingDate: new Date(),
      exchangeRate: 1,
      category: requestedCategory,
      countryId: tenantCountryId,
      companyId: this.toolbarService.customer()?.company?.id ?? null
    });
    this.addressForm.patchValue({ countryId: tenantCountryId });
    this.loadCitiesForCountry(tenantCountryId);
    this.setBreadcrumb('New Client');
  }

  ngOnDestroy(): void {
    const oldUrl = this.queuedLogo()?.previewUrl;
    if (oldUrl) {
      URL.revokeObjectURL(oldUrl);
    }
  }

  loadEmployees() {
    const companyId = this.toolbarService.customer()?.company?.id;
    if (!companyId) return;
    this.partyService.getParties(companyId, ['Employee'], '', 0, 500).subscribe(page => {
      this.employeeOptions.set((page.content || []).map(party => ({
        label: party.entityName,
        value: party.id.entityId
      })));
    });
  }

  loadRoles() {
    this.crmService.getCrmRoles().subscribe({
      next: roles => this.crmRoles.set(roles || []),
      error: () => this.crmRoles.set([])
    });
  }

  loadPaymentTerms() {
		const owningCompanyId = this.clientForm.get('companyId')?.value ?? this.toolbarService.customer()?.company?.id;
    this.ordersService.getAllPaymentTerms(owningCompanyId ?? undefined).subscribe({
      next: terms => this.paymentTerms.set(terms || []),
      error: () => this.paymentTerms.set([])
    });
  }

  loadCountries() {
    const cachedCountries = this.sessionStorageService.getCountryList() ?? [];
    if (cachedCountries.length) {
      this.countries.set(cachedCountries);
      this.restoreCountrySelection();
    }
    this.baseMasterDataService.getCountries('en-US').subscribe(countries => {
      if (countries?.length) {
        this.countries.set(countries);
        this.restoreCountrySelection();
        const countryId = this.addressForm.get('countryId')?.value || this.clientForm.get('countryId')?.value || this.toolbarService.customer()?.company?.countryId;
        const currency = countries.find(country => country.id === countryId)?.currencyCode || 'SAR';
        if (!this.clientForm.get('currencyCode')?.value) this.clientForm.patchValue({ currencyCode: currency });
        if (countryId) {
          this.loadCitiesForCountry(countryId);
        }
      }
    });
  }

  loadClient(id: number) {
    this.loading.set(true);
    forkJoin({
      client: this.crmService.getClient(id),
      address: this.crmService.getClientAddress(id),
      communications: this.crmService.getClientMasterCommunications(id),
      documents: this.crmService.getClientDocuments(id),
      registrationAttachments: this.crmService.getClientRegistrationAttachments(id)
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: ({ client, address, communications, documents, registrationAttachments }) => {
        const clientCountryId = this.numericId(client.data?.countryId);
        if (client.success && client.data) {
          this.clientForm.patchValue({
            ...client.data,
            countryId: clientCountryId
          });
          this.setBreadcrumb(client.data.companyName || 'Edit Client');
        }
        const addressCountryId = this.numericId(address?.countryId) ?? clientCountryId;
        if (address) {
          this.addressForm.patchValue({
            ...address,
            countryId: addressCountryId,
            cityId: this.numericId(address.cityId)
          }, { emitEvent: false });
        } else if (addressCountryId) {
          this.addressForm.patchValue({ countryId: addressCountryId }, { emitEvent: false });
        }
        if (addressCountryId) {
          this.clientForm.patchValue({ countryId: addressCountryId }, { emitEvent: false });
          this.restoreCountrySelection();
          this.loadCitiesForCountry(addressCountryId, () => this.patchAddressCityByName(address?.city));
        }
        this.documents.set(documents || []);
        this.registrationAttachments.set(registrationAttachments || []);
        this.loadedCommunicationIds = new Set((communications || []).map(row => row.id).filter((id): id is number => !!id));
        this.resetContacts((communications || []).length ? communications : [this.primaryContactFromClient(client.data)]);
        this.syncBusinessTypeValidators();
        this.syncCreditHoldValidator();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Clients', detail: 'Unable to load client.' })
    });
  }

  saveClient() {
    if (this.saving()) {
      return;
    }
    this.submitted.set(true);
    this.syncBusinessTypeValidators();
    if (this.clientForm.invalid || this.addressForm.invalid) {
      this.clientForm.markAllAsTouched();
      this.addressForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }
    if (this.defaultPaymentTermExceedsCreditPeriod()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Credit Control',
        detail: 'Default payment term cannot exceed the approved credit period.'
      });
      return;
    }

    const payload = this.clientForm.value as CrmClient;
    payload.companyId = this.toolbarService.customer()?.company?.id ?? payload.companyId;
    payload.postalCode = payload.postalCode || this.addressForm.get('postalCode')?.value || '';
    payload.countryId = payload.countryId || this.addressForm.get('countryId')?.value || undefined;
    if (!payload.id) {
      payload.createdByCustomerId = this.toolbarService.customer()?.id ?? payload.createdByCustomerId;
    } else {
      payload.updatedByCustomerId = this.toolbarService.customer()?.id ?? payload.updatedByCustomerId;
    }

    this.saving.set(true);
    this.uploadQueuedLogo().pipe(
      switchMap(uploadedLogoUrl => {
        if (uploadedLogoUrl) {
          payload.logoUrl = uploadedLogoUrl;
          this.clientForm.patchValue({ logoUrl: uploadedLogoUrl }, { emitEvent: false });
        }
        return payload.id ? this.crmService.updateClient(payload) : this.crmService.addClient(payload);
      }),
      switchMap(response => {
        if (!response.success || !response.data?.id) {
          return of(response);
        }
        const clientId = response.data.id;
        const companyId = response.data.companyId;
        const addressValue = this.addressForm.getRawValue();
        const address = {
          ...addressValue,
          city: this.cityById(addressValue.cityId)?.cityName || addressValue.city || '',
          postalCode: this.addressForm.get('postalCode')?.value || payload.postalCode
        } as CrmClientAddress;
        const communicationRows = this.contactPayloads(payload);
        const retainedCommunicationIds = new Set(communicationRows.map(row => row.id).filter((id): id is number => !!id));
        const deleteRequests = Array.from(this.loadedCommunicationIds)
          .filter(id => !retainedCommunicationIds.has(id))
          .map(id => this.crmService.deleteClientMasterCommunication(clientId, id, companyId));
        const relatedRequests = [
          this.crmService.saveClientAddress(clientId, address, companyId),
          ...communicationRows.map(row => this.crmService.saveClientMasterCommunication(clientId, row, companyId)),
          ...deleteRequests
        ];
        return forkJoin({
          response: of(response),
          relatedRecords: forkJoin(relatedRequests),
          attachments: this.uploadPendingAttachments(clientId, companyId)
        });
      }),
      finalize(() => this.saving.set(false))
    ).subscribe({
      next: result => {
        const response = 'response' in result ? result.response : result;
        if (response.success && response.data?.id) {
          const oldPreviewUrl = this.queuedLogo()?.previewUrl;
          if (oldPreviewUrl) {
            URL.revokeObjectURL(oldPreviewUrl);
          }
          this.queuedLogo.set(null);
          const attachmentResults = 'attachments' in result ? result.attachments : [];
          const successfulQueueIds = new Set(attachmentResults.filter(upload => upload.success).map(upload => upload.queueId));
          const failedAttachmentCount = attachmentResults.filter(upload => !upload.success).length;
          this.pendingAttachments.update(rows => rows.filter(row => !successfulQueueIds.has(row.queueId)));
          this.appendUploadedAttachments(attachmentResults);

          if (failedAttachmentCount > 0) {
            this.clientId.set(response.data.id);
            this.clientForm.patchValue({
              id: response.data.id,
              companyId: response.data.companyId ?? payload.companyId
            });
            this.messageService.add({
              severity: 'warn',
              summary: 'Client Saved',
              detail: `${failedAttachmentCount} attachment${failedAttachmentCount === 1 ? '' : 's'} could not be uploaded. The failed file${failedAttachmentCount === 1 ? ' remains' : 's remain'} queued so you can save again.`
            });
            return;
          }

          this.messageService.add({ severity: 'success', summary: 'Client Saved', detail: 'Customer master details and optional attachments saved.' });
          const returnUrl = this.returnUrl();
          if (returnUrl) {
            this.router.navigateByUrl(returnUrl);
          } else {
            this.router.navigate(['/crm/clients', response.data.id]);
          }
        }
      },
      error: error => {
        const detail = error?.message || error?.error?.message || error?.error?.detail || 'The CRM client could not be saved.';
        this.messageService.add({ severity: 'error', summary: 'Client Save Failed', detail });
      }
    });
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid File', detail: 'Please select an image file (PNG, JPG, SVG, etc.).' });
      input.value = '';
      return;
    }
    const oldUrl = this.queuedLogo()?.previewUrl;
    if (oldUrl) {
      URL.revokeObjectURL(oldUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    this.queuedLogo.set({
      file,
      previewUrl,
      progress: 0,
      state: 'queued'
    });
    input.value = '';
  }

  removeQueuedLogo(input?: HTMLInputElement): void {
    const oldUrl = this.queuedLogo()?.previewUrl;
    if (oldUrl) {
      URL.revokeObjectURL(oldUrl);
    }
    this.queuedLogo.set(null);
    if (input) {
      input.value = '';
    }
  }

  removeExistingLogo(): void {
    this.clientForm.patchValue({ logoUrl: '' });
  }

  get currentLogoUrl(): string {
    return this.clientForm.get('logoUrl')?.value || '';
  }

  private uploadQueuedLogo(): Observable<string | null> {
    const queued = this.queuedLogo();
    if (!queued) {
      return of(null);
    }
    this.queuedLogo.set({ ...queued, state: 'uploading' });
    const storagePath = 'crm/client-logos';
    return new Observable<string>(observer => {
      this.firebaseStorageService.uploadFileWithProgress(queued.file, storagePath).subscribe({
        next: progress => {
          const item = this.queuedLogo();
          if (item) {
            this.queuedLogo.set({ ...item, progress: progress.progress });
          }
          if (progress.url) {
            if (item) {
              this.queuedLogo.set({ ...item, progress: 100, state: 'uploaded' });
            }
            observer.next(progress.url);
            observer.complete();
          }
        },
        error: err => {
          const item = this.queuedLogo();
          if (item) {
            this.queuedLogo.set({ ...item, state: 'error' });
          }
          observer.error(err);
        }
      });
    });
  }

  queueAttachments(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    const category = this.attachmentForm.get('category')?.value || 'GENERAL';
    const option = this.attachmentTypes.find(type => type.value === category) || this.attachmentTypes[0];
    const title = this.attachmentForm.get('title')?.value?.trim() || '';
    const expiryDate = this.attachmentForm.get('expiryDate')?.value || undefined;
    const queued = files.map((file, index): PendingClientAttachment => ({
      queueId: `${Date.now()}-${++this.attachmentSequence}`,
      file,
      category: option.value,
      categoryLabel: option.label,
      source: option.source,
      title: files.length === 1 ? (title || file.name) : file.name,
      expiryDate: option.source === 'document' ? expiryDate : undefined
    }));

    this.pendingAttachments.update(rows => [...rows, ...queued]);
    this.attachmentForm.patchValue({ title: '', expiryDate: null });
    input.value = '';
  }

  removePendingAttachment(queueId: string) {
    if (this.saving()) return;
    this.pendingAttachments.update(rows => rows.filter(row => row.queueId !== queueId));
  }

  selectedAttachmentSource(): ClientAttachmentSource {
    const category = this.attachmentForm.get('category')?.value;
    return this.attachmentTypes.find(type => type.value === category)?.source || 'document';
  }

  attachmentCategoryLabel(category?: string): string {
    return this.attachmentTypes.find(type => type.value === category)?.label || category || 'Document';
  }

  fileSizeLabel(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  attachmentCount(category: string): number {
    return this.documents().filter(document => document.category === category).length
      + this.registrationAttachments().filter(attachment => attachment.category === category).length
      + this.pendingAttachments().filter(attachment => attachment.category === category).length;
  }

  get contactRows(): FormArray {
    return this.communicationsForm.get('contacts') as FormArray;
  }

  addContact(contact?: Partial<CrmClientCommunication>) {
    this.contactRows.push(this.createContactGroup(contact));
    this.ensureOnePrimaryContact();
  }

  removeContact(index: number) {
    if (this.contactRows.length <= 1) {
      this.contactRows.at(0)?.patchValue({
        id: null,
        email: '',
        communicationType: 'PRIMARY',
        name: '',
        phone: '',
        mobile: '',
        extension: '',
        fax: '',
        website: '',
        primaryRecord: true
      });
      return;
    }
    this.contactRows.removeAt(index);
    this.ensureOnePrimaryContact();
  }

  setPrimaryContact(index: number) {
    this.contactRows.controls.forEach((group, currentIndex) => {
      group.patchValue({ primaryRecord: currentIndex === index }, { emitEvent: false });
    });
  }

  cancel() {
    const id = this.clientForm.get('id')?.value;
    const returnUrl = this.returnUrl();
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(id ? ['/crm/clients', id] : ['/crm/clients']);
    }
  }

  syncBusinessTypeValidators() {
    const isSupplier = this.hasSupplierRole(this.clientForm.get('roles')?.value);
    this.supplierRoleSelected.set(isSupplier);
    this.customerRoleSelected.set(this.hasCustomerRole(this.clientForm.get('roles')?.value));
    const isB2B = !isSupplier && this.clientForm.get('businessType')?.value === 'B2B';

    this.setControlValidators(this.clientForm.get('companyName'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.clientForm.get('customerNameLocal'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.clientForm.get('notifyingPersonName'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.clientForm.get('postingDate'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.clientForm.get('currencyCode'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.clientForm.get('exchangeRate'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.clientForm.get('businessType'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.clientForm.get('primaryEmail'), isSupplier ? [Validators.email] : [Validators.required, Validators.email]);

    const taxControl = this.clientForm.get('taxRegNumber');
    taxControl?.setValidators(isB2B
      ? [Validators.required, gccVatNumberValidator(() => this.selectedCountry())]
      : [gccVatNumberValidator(() => this.selectedCountry())]);
    taxControl?.updateValueAndValidity({ emitEvent: false });

    const crControl = this.clientForm.get('crNumber');
    crControl?.setValidators(isB2B
      ? [Validators.required, gccCrNumberValidator(() => this.selectedCountry())]
      : [gccCrNumberValidator(() => this.selectedCountry())]);
    crControl?.updateValueAndValidity({ emitEvent: false });

    const postalCodeControl = this.addressForm.get('postalCode');
    postalCodeControl?.setValidators(isB2B ? [Validators.required] : []);
    postalCodeControl?.updateValueAndValidity({ emitEvent: false });

    this.setControlValidators(this.addressForm.get('street1'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.addressForm.get('street1Local'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.addressForm.get('cityId'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.addressForm.get('cityLocal'), isSupplier ? [] : [Validators.required]);
    this.setControlValidators(this.addressForm.get('countryId'), isSupplier ? [] : [Validators.required]);
  }

  syncCreditHoldValidator() {
    const reasonControl = this.clientForm.get('creditHoldReason');
    reasonControl?.setValidators(this.clientForm.get('creditHold')?.value ? [Validators.required] : []);
    reasonControl?.updateValueAndValidity({ emitEvent: false });
  }

  hasClientError(controlName: string) {
    const control = this.clientForm.get(controlName);
    return !!control && (this.submitted() || control.touched) && control.invalid;
  }

  hasAddressError(controlName: string) {
    const control = this.addressForm.get(controlName);
    return !!control && (this.submitted() || control.touched) && control.invalid;
  }

  customerMasterLinkStatus(): string {
    const linkedCompanyId = this.clientForm.get('linkedCompanyId')?.value;
    const linkedCustomerId = this.clientForm.get('linkedCustomerId')?.value;
    if (linkedCompanyId && linkedCustomerId) {
      return 'Linked and ready for sales orders.';
    }
    return this.clientId()
      ? 'Not linked yet. Saving this client will create or reuse its customer master records.'
      : 'The company and customer master records will be created or linked when you save this client.';
  }

  defaultPaymentTermExceedsCreditPeriod(): boolean {
    const creditPeriodDays = this.clientForm.get('creditPeriodDays')?.value;
    const defaultPaymentTermId = this.clientForm.get('defaultPaymentTermId')?.value;
    const paymentTermDays = this.paymentTerms().find(term => term.paymentTermID === defaultPaymentTermId)?.days;
    return creditPeriodDays !== null && creditPeriodDays !== undefined
      && paymentTermDays !== null && paymentTermDays !== undefined
      && paymentTermDays > creditPeriodDays;
  }

  clientErrorMessage(controlName: string) {
    const control = this.clientForm.get(controlName);
    if (!control || !(this.submitted() || control.touched)) return null;
    if (control.hasError('required')) {
      if (controlName === 'roles') return 'Select at least one business role.';
      return controlName === 'taxRegNumber' || controlName === 'crNumber'
        ? 'Required for B2B clients.'
        : 'This field is required.';
    }
    if (control.hasError('email')) return 'Enter a valid email address.';
    return gccRegistrationError(control);
  }

  addressErrorMessage(controlName: string) {
    const control = this.addressForm.get(controlName);
    if (!control || !(this.submitted() || control.touched)) return null;
    if (control.hasError('required')) {
      if (controlName === 'cityId') return 'City is required.';
      if (controlName === 'cityLocal') return 'Local city is required.';
      if (controlName === 'street1') return 'Street 1 is required.';
      if (controlName === 'street1Local') return 'Local street is required.';
      if (controlName === 'countryId') return 'Country is required.';
      if (controlName === 'postalCode') return 'Required for B2B clients.';
      return 'This field is required.';
    }
    return null;
  }

  addressCityOptions() {
    const countryId = this.addressForm.get('countryId')?.value;
    if (!countryId) return [];
    return this.cities().filter(city => city.country?.id === countryId);
  }

  private createContactGroup(contact?: Partial<CrmClientCommunication>): FormGroup {
    return this.fb.group({
      id: [contact?.id ?? null],
      email: [contact?.email ?? ''],
      communicationType: [contact?.communicationType ?? (contact?.primaryRecord ? 'PRIMARY' : 'OPERATION')],
      name: [contact?.name ?? ''],
      phone: [contact?.phone ?? ''],
      mobile: [contact?.mobile ?? ''],
      extension: [contact?.extension ?? ''],
      fax: [contact?.fax ?? ''],
      website: [contact?.website ?? ''],
      primaryRecord: [contact?.primaryRecord ?? false]
    });
  }

  private resetContacts(contacts: Partial<CrmClientCommunication>[]) {
    this.contactRows.clear();
    contacts.forEach(contact => this.contactRows.push(this.createContactGroup(contact)));
    if (this.contactRows.length === 0) {
      this.addContact({ communicationType: 'PRIMARY', primaryRecord: true });
    }
    this.ensureOnePrimaryContact();
  }

  private ensureOnePrimaryContact() {
    if (this.contactRows.length === 0) return;
    const primaryIndex = this.contactRows.controls.findIndex(group => !!group.get('primaryRecord')?.value);
    if (primaryIndex >= 0) {
      this.setPrimaryContact(primaryIndex);
      return;
    }
    this.setPrimaryContact(0);
  }

  private contactPayloads(client: CrmClient): CrmClientCommunication[] {
    this.ensureOnePrimaryContact();
    const rows = this.contactRows.getRawValue() as CrmClientCommunication[];
    const contacts: CrmClientCommunication[] = rows
      .map(row => ({
        ...row,
        communicationType: row.communicationType || (row.primaryRecord ? 'PRIMARY' : 'OPERATION'),
        name: row.name?.trim() || '',
        email: row.email?.trim() || '',
        phone: row.phone?.trim() || '',
        mobile: row.mobile?.trim() || '',
        extension: row.extension?.trim() || '',
        fax: row.fax?.trim() || '',
        website: row.website?.trim() || ''
      }))
      .filter(row => !!(row.id || row.name || row.email || row.phone || row.mobile || row.extension || row.fax || row.website));

    const hasPrimary = contacts.some(row => row.primaryRecord);
    if (contacts.length === 0) {
      contacts.push(this.primaryContactFromClient(client));
    } else if (!hasPrimary) {
      contacts[0].primaryRecord = true;
    }
    return contacts.map((row, index) => ({
      ...row,
      primaryRecord: index === contacts.findIndex(contact => contact.primaryRecord)
    }));
  }

  private uploadPendingAttachments(clientId: number, companyId?: number) {
    const pending = this.pendingAttachments();
    if (!pending.length) {
      return of([] as ClientAttachmentUploadResult[]);
    }
    return forkJoin(pending.map(attachment => this.uploadPendingAttachment(attachment, clientId, companyId)));
  }

  private uploadPendingAttachment(attachment: PendingClientAttachment, clientId: number, companyId?: number) {
    const path = attachment.source === 'registration'
      ? `crm/client-registration/${clientId}`
      : `crm/client-documents/${clientId}`;
    return this.firebaseStorageService.uploadFileAndGetPublicUrl(attachment.file, path).pipe(
      switchMap(fileUrl => {
        if (attachment.source === 'registration') {
          const registrationAttachment: CrmClientRegistrationAttachment = {
            category: attachment.category as CrmRegistrationAttachmentCategory,
            fileName: attachment.file.name,
            fileUrl,
            fileType: attachment.file.type,
            fileSize: attachment.file.size,
            uploadedByCustomerId: this.toolbarService.customer()?.id
          };
          return this.crmService.saveClientRegistrationAttachment(clientId, registrationAttachment, companyId);
        }
        const document: CrmClientDocument = {
          category: attachment.category,
          title: attachment.title || attachment.file.name,
          fileName: attachment.file.name,
          fileUrl,
          fileType: attachment.file.type,
          fileSize: attachment.file.size,
          expiryDate: attachment.expiryDate,
          uploadedByCustomerId: this.toolbarService.customer()?.id
        };
        return this.crmService.saveClientDocument(clientId, document, companyId);
      }),
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Attachment metadata could not be saved.');
        }
        return {
          queueId: attachment.queueId,
          source: attachment.source,
          success: true,
          record: response.data
        } as ClientAttachmentUploadResult;
      }),
      catchError(() => of({
        queueId: attachment.queueId,
        source: attachment.source,
        success: false
      } as ClientAttachmentUploadResult))
    );
  }

  private appendUploadedAttachments(results: ClientAttachmentUploadResult[]) {
    const documents = results
      .filter(result => result.success && result.source === 'document' && result.record)
      .map(result => result.record as CrmClientDocument);
    const registrationAttachments = results
      .filter(result => result.success && result.source === 'registration' && result.record)
      .map(result => result.record as CrmClientRegistrationAttachment);
    if (documents.length) {
      this.documents.update(rows => [...rows, ...documents]);
    }
    if (registrationAttachments.length) {
      this.registrationAttachments.update(rows => [...rows, ...registrationAttachments]);
    }
  }

  private primaryContactFromClient(client: CrmClient): CrmClientCommunication {
    return {
      communicationType: 'PRIMARY',
      name: client.notifyingPersonName || '',
      email: client.primaryEmail || '',
      phone: client.phoneNumber || '',
      mobile: client.mobileNumber || '',
      website: client.website || '',
      primaryRecord: true
    };
  }

  cityLabel(city?: City | number | null): string {
    if (typeof city === 'number') {
      city = this.cityById(city);
    }
    return city ? [city.cityName, city.country?.name].filter(Boolean).join(', ') : '';
  }

  syncSelectedCity(cityId?: number | null) {
    this.addressForm.patchValue({ city: this.cityById(cityId)?.cityName || '' }, { emitEvent: false });
  }

  setBreadcrumb(label: string) {
    this.breadcrumbItems.set([
      { label: 'CRM', routerLink: '/crm/clients' },
      { label: 'Clients', routerLink: '/crm/clients' },
      { label }
    ]);
  }

  private selectedCountry(): Country | null {
    const countryId = this.addressForm.get('countryId')?.value || this.clientForm.get('countryId')?.value;
    return this.countries().find(country => country.id === countryId) ?? null;
  }

  private loadCitiesForCountry(countryId?: number | null, afterLoad?: () => void) {
    if (!countryId) {
      afterLoad?.();
      return;
    }
    if (this.loadedCityCountryIds.has(countryId)) {
      afterLoad?.();
      return;
    }
    if (this.loadingCityCountryIds.has(countryId)) return;

    this.loadingCityCountryIds.add(countryId);
    this.baseMasterDataService.getCities('en-US', countryId).subscribe({
      next: cities => {
        const existingById = new Map(this.cities().map(city => [city.id, city]));
        (cities ?? []).forEach(city => existingById.set(city.id, city));
        this.cities.set(Array.from(existingById.values()).sort((a, b) => (a.cityName || '').localeCompare(b.cityName || '')));
        this.loadedCityCountryIds.add(countryId);
        this.loadingCityCountryIds.delete(countryId);
        afterLoad?.();
      },
      error: () => {
        this.loadingCityCountryIds.delete(countryId);
        afterLoad?.();
      }
    });
  }

  private cityById(cityId?: number | null) {
    return this.cities().find(city => city.id === cityId);
  }

  private cityBelongsToCountry(cityId?: number | null, countryId?: number | null) {
    if (!cityId || !countryId) return false;
    return this.cityById(cityId)?.country?.id === countryId;
  }

  private patchAddressCityByName(cityName?: string | null) {
    const currentCityId = this.numericId(this.addressForm.get('cityId')?.value);
    if (currentCityId) {
      const cityById = this.addressCityOptions().find(city => Number(city.id) === currentCityId);
      if (cityById) {
        this.addressForm.patchValue({ cityId: cityById.id, city: cityById.cityName }, { emitEvent: false });
        return;
      }
    }
    const normalizedCity = String(cityName || '').trim().toLowerCase();
    if (!normalizedCity) return;
    const matchedCity = this.addressCityOptions().find(city => {
      const label = this.cityLabel(city).toLowerCase();
      const name = (city.cityName || '').trim().toLowerCase();
      return normalizedCity === name || normalizedCity === label || normalizedCity.startsWith(`${name},`);
    });
    if (matchedCity) {
      this.addressForm.patchValue({ cityId: matchedCity.id, city: matchedCity.cityName }, { emitEvent: false });
    }
  }

  private restoreCountrySelection() {
    const countryId = this.numericId(this.addressForm.get('countryId')?.value)
      ?? this.numericId(this.clientForm.get('countryId')?.value);
    if (!countryId) return;
    const selectedCountry = this.countries().find(country => Number(country.id) === countryId);
    const normalizedCountryId = selectedCountry?.id ?? countryId;
    this.addressForm.patchValue({ countryId: normalizedCountryId }, { emitEvent: false });
    this.clientForm.patchValue({ countryId: normalizedCountryId }, { emitEvent: false });
  }

  private numericId(value?: number | string | null): number | null {
    if (value === null || value === undefined || value === '') return null;
    const id = Number(value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private hasSupplierRole(roles?: string[] | null): boolean {
    return (roles || []).some(role => `${role || ''}`.toUpperCase().includes('SUPPLIER'));
  }

  private hasCustomerRole(roles?: string[] | null): boolean {
    return (roles || []).some(role => `${role || ''}`.toUpperCase() === 'CUSTOMER');
  }

  private setControlValidators(control: AbstractControl | null, validators: ValidatorFn[]): void {
    control?.setValidators(validators);
    control?.updateValueAndValidity({ emitEvent: false });
  }
}
